// Pantalla admin: tabla de vocabulary items con filtros + drawer editor inline.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { Spinner } from '@/components/atoms/Spinner';
import { Header } from '@/components/organisms/Header';
import { VocabularyEditorDrawer } from '@/components/organisms/VocabularyEditorDrawer';
import { ApiError } from '@/services/api';
import { listLevels, type CatalogLevel } from '@/services/catalogs';
import {
  adminDeleteVocabulary,
  adminListVocabulary,
  type VocabularyItem,
} from '@/services/vocabulary';
import styles from './AdminVocabulary.module.scss';

const PAGE_SIZE = 50;

export function AdminVocabulary() {
  const [items, setItems] = useState<VocabularyItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [levels, setLevels] = useState<CatalogLevel[]>([]);

  const [levelFilter, setLevelFilter] = useState<number | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(true);
  const [page, setPage] = useState(0);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<VocabularyItem | null>(null);

  useEffect(() => {
    listLevels().then(setLevels).catch(() => {});
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await adminListVocabulary({
        level_id: levelFilter,
        search: search.trim() || undefined,
        include_inactive: includeInactive,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setItems(r.items);
      setTotal(r.total);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
    } finally {
      setLoading(false);
    }
  }, [levelFilter, search, includeInactive, page]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Reset page si cambian filtros principales.
  useEffect(() => {
    setPage(0);
  }, [levelFilter, includeInactive]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  async function handleDelete(item: VocabularyItem) {
    if (!confirm(`¿Borrar "${item.word}"? Quedará archivado y dejará de aparecer.`)) return;
    try {
      await adminDeleteVocabulary(item.id);
      void reload();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
    }
  }

  function openCreate() {
    setEditing(null);
    setEditorOpen(true);
  }

  function openEdit(item: VocabularyItem) {
    setEditing(item);
    setEditorOpen(true);
  }

  return (
    <>
      <Header
        title="Vocabulary"
        actions={
          <Button variant="primary" onClick={openCreate}>
            <Icon name="plus" size={14} /> Nueva palabra
          </Button>
        }
      />
      <main className={styles.main}>
        <section className={styles.filters}>
          <label className={styles.field}>
            <span>Nivel</span>
            <select value={levelFilter ?? ''} onChange={(e) => setLevelFilter(e.target.value ? Number(e.target.value) : undefined)}>
              <option value="">Todos</option>
              {levels.map((l) => (
                <option key={l.id} value={l.id}>{l.label}</option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            <span>Buscar</span>
            <form onSubmit={(e) => { e.preventDefault(); setPage(0); void reload(); }}>
              <input
                type="search"
                placeholder="palabra o traducción"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </form>
          </label>
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            <span>Incluir inactivas</span>
          </label>
        </section>

        {loading ? (
          <div className={styles.center}><Spinner size="lg" /></div>
        ) : error ? (
          <p className={styles.error}>{error}</p>
        ) : items.length === 0 ? (
          <div className={styles.empty}>
            <h3>Sin resultados</h3>
            <p>No hay palabras con esos filtros.</p>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Palabra</th>
                <th>Traducción</th>
                <th>Pronunciación</th>
                <th>Nivel</th>
                <th>Licencia</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td>
                    <div className={styles.wordCell}>
                      {it.image_url && <img src={it.image_url} alt="" className={styles.thumb} />}
                      <span className={styles.word}>{it.word}</span>
                    </div>
                  </td>
                  <td>{it.translation ?? <span className={styles.muted}>—</span>}</td>
                  <td>
                    {it.pronunciation ? <span className={styles.mono}>{it.pronunciation}</span> : <span className={styles.muted}>—</span>}
                  </td>
                  <td>{it.level_label ? <Badge tone="accent">{it.level_label}</Badge> : <span className={styles.muted}>—</span>}</td>
                  <td>{it.license_slug ?? <span className={styles.muted}>Todas</span>}</td>
                  <td>
                    {it.is_active === 1 ? (
                      <Badge tone="success">Activa</Badge>
                    ) : (
                      <Badge tone="neutral">Inactiva</Badge>
                    )}
                  </td>
                  <td className={styles.actions}>
                    <Button variant="secondary" size="sm" onClick={() => openEdit(it)}>Editar</Button>
                    <Button variant="secondary" size="sm" onClick={() => handleDelete(it)}>
                      <Icon name="trash" size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className={styles.pagination}>
          <span className={styles.paginationInfo}>
            {total} palabra{total === 1 ? '' : 's'} — página {page + 1} de {totalPages}
          </span>
          <div className={styles.paginationActions}>
            <Button variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
              <Icon name="chevron-left" size={14} /> Anterior
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
              Siguiente <Icon name="chevron-right" size={14} />
            </Button>
          </div>
        </div>
      </main>

      <VocabularyEditorDrawer
        open={editorOpen}
        onOpenChange={setEditorOpen}
        item={editing}
        onSaved={() => {
          setEditorOpen(false);
          void reload();
        }}
      />
    </>
  );
}
